import type { Company } from '@/scenes/authentication/types/types.auth';
import type { SearchCompaniesDTO } from '@/scenes/company/company-search/api/api';
import SearchIcon from '@mui/icons-material/Search';
import { Box, Button, CircularProgress, ClickAwayListener, Paper, TextField, Typography } from '@mui/material';
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDebounce } from 'use-debounce';
import SignUpOrLogInModal from '@/scenes/authentication/components/SignUpOrLogInModal';
import { fetchCompanies } from '@/scenes/company/company-search/api/api';
import { CreateCompanyModal } from './Modals/CreateCompanyModal';
import ReviewDialog from '@/scenes/review/components/ReviewDialog';
import { useReviewContext } from '@/scenes/review/context/ReviewContext';
import { trackEvent } from '@/third-party/ga';
import { UserContext } from '@/shared/contexts/UserContext';
import { OnboardingAction, storeOnboardingIntent } from '../utils/onboardingIntent';
import CompanyLogo from './CompanyLogo';
import { log } from '../utils/log';

interface NavbarCompanySearchProps {
  onCompanySelect?: (company: Company) => void;
}

const NavbarCompanySearch: React.FC<NavbarCompanySearchProps> = ({ onCompanySelect }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm] = useDebounce(searchTerm, 300);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showAddCompanyModal, setShowAddCompanyModal] = useState(false);
  const [addCompanyIntent, setAddCompanyIntent] = useState<'follow' | 'review'>('follow');
  const [openReviewDialog, setOpenReviewDialog] = useState(false);
  const [reviewCompany, setReviewCompany] = useState<{ companyName: string; companyId: number } | null>(null);
  const [isAddCompanyFlow, setIsAddCompanyFlow] = useState(false);

  const { user } = useContext(UserContext);
  const { resetReviewState } = useReviewContext();
  const navigate = useNavigate();

  useEffect(() => {
    const searchCompanies = async () => {
      if (debouncedSearchTerm.trim() === '') {
        setCompanies([]);
        return;
      }

      setLoading(true);
      try {
        const searchCompaniesDTO: SearchCompaniesDTO = {
          limit: 10,
          orderBy: 'companyName',
          orderDirection: 'asc',
          searchTerm: debouncedSearchTerm,
        };

        const response = await fetchCompanies(searchCompaniesDTO);
        setCompanies(response.results);

        trackEvent({
          eventName: 'navbar_company_search',
          eventCategory: 'Navigation',
          eventLabel: debouncedSearchTerm,
          eventValue: response.results.length,
        });
      } catch (error) {
        log.error('Error fetching companies:', error);
        setCompanies([]);
      } finally {
        setLoading(false);
      }
    };

    searchCompanies();
  }, [debouncedSearchTerm]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchTerm(value);

    if (value.trim() !== '' && !open) {
      setOpen(true);
    }
  };

  const handleCompanySelect = (company: Company) => {
    setOpen(false);
    setSearchTerm('');

    if (onCompanySelect) {
      onCompanySelect(company);
    } else {
      navigate(`/company/${encodeURIComponent(company.companyName || '')}`);
    }
  };

  const handleAddCompanyAndFollow = () => {
    setOpen(false);

    if (!user?.idToken) {
      storeOnboardingIntent({
        action: OnboardingAction.CreateReview,
        returnUrl: window.location.pathname,
        isAddCompanyFlow: true,
      });
      setIsAddCompanyFlow(true);
      setShowAuthModal(true);
    } else {
      setAddCompanyIntent('follow');
      setShowAddCompanyModal(true);
    }
  };

  const handleAddCompanyAndReview = () => {
    setOpen(false);

    if (!user?.idToken) {
      storeOnboardingIntent({
        action: OnboardingAction.CreateReview,
        returnUrl: window.location.pathname,
        isAddCompanyReview: true,
      });
      setIsAddCompanyFlow(false);
      setShowAuthModal(true);
    } else {
      setAddCompanyIntent('review');
      setShowAddCompanyModal(true);
    }
  };

  const handleAuthSuccess = async () => {
    setShowAuthModal(false);
  };

  const handleClickAway = () => {
    setOpen(false);
  };

  const showNoResults = debouncedSearchTerm.trim() !== '' && companies.length === 0 && !loading;

  return (
    <>
      <ClickAwayListener onClickAway={handleClickAway}>
        <Box
          sx={{
            position: 'relative',
            width: { xs: '200px', sm: '240px', md: '300px' },
          }}
        >
          <TextField
            size="small"
            placeholder="Search companies..."
            value={searchTerm}
            onChange={handleInputChange}
            onFocus={() => {
              if (searchTerm.trim() !== '') {
                setOpen(true);
              }
            }}
            className="input input--search-navbar"
            InputProps={{
              startAdornment: <SearchIcon sx={{ color: 'grey.500', mr: 1 }} />,
              endAdornment: loading ? <CircularProgress size={20} /> : null,
            }}
            data-testid="navbar-company-search"
          />

          {open && (
            <Paper className="search-dropdown">
              {loading && (
                <Box className="search-dropdown__loading">
                  <CircularProgress size={24} />
                </Box>
              )}

              {companies.length > 0 && (
                <>
                  {companies.map((company, index) => (
                    <Box
                      key={company.id || index}
                      className="search-result-item"
                      onClick={() => handleCompanySelect(company)}
                      data-testid={`navbar-search-company-${index}`}
                    >
                      <CompanyLogo
                        companyName={company.companyName || ''}
                        companyId={company.id}
                        className="search-result-item__logo"
                      />
                      <Box className="search-result-item__content">
                        <Typography className="search-result-item__name">{company.companyName}</Typography>
                        <Typography className="search-result-item__sector">
                          {company.gicsSector || 'No sector available'}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </>
              )}

              {showNoResults && (
                <Box className="search-dropdown__no-results">
                  <Typography className="search-dropdown__no-results-title">
                    Don't see the company you're looking for?
                  </Typography>

                  <Box className="search-dropdown__no-results-actions">
                    <Button
                      fullWidth
                      variant="outlined"
                      size="small"
                      onClick={handleAddCompanyAndFollow}
                      data-testid="navbar-search-add-company-follow"
                      className="button-rounded button-rounded--outlined-primary"
                    >
                      👀 Add Company & Follow
                    </Button>

                    <Button
                      fullWidth
                      variant="contained"
                      size="small"
                      onClick={handleAddCompanyAndReview}
                      data-testid="navbar-search-add-company-review"
                      className="button-rounded button-rounded--primary"
                    >
                      ✍️ Add Company & Leave a Deal Retro
                    </Button>
                  </Box>
                </Box>
              )}
            </Paper>
          )}
        </Box>
      </ClickAwayListener>

      <SignUpOrLogInModal
        open={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        contextMessage={
          isAddCompanyFlow
            ? 'Create an account to add a company and follow for updates'
            : 'Create an account to leave a deal retro'
        }
        handleAuthSuccess={handleAuthSuccess}
        initialMode="signup"
      />

      <ReviewDialog
        open={openReviewDialog}
        companyName={reviewCompany?.companyName}
        companyId={reviewCompany?.companyId}
        setOpen={setOpenReviewDialog}
      />

      <CreateCompanyModal
        open={showAddCompanyModal}
        onClose={() => setShowAddCompanyModal(false)}
        onCompanyCreated={(company) => {
          if (addCompanyIntent === 'review') {
            resetReviewState();
            setReviewCompany({
              companyName: company.companyName,
              companyId: company.id,
            });
            setOpenReviewDialog(true);
          } else {
            navigate(`/company/${encodeURIComponent(company.companyName)}`);
          }
        }}
      />
    </>
  );
};

export default NavbarCompanySearch;
